import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
    Label,
    FormGroup
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import TableContainer from "../../Components/Common/TableContainer";
import { useNavigate } from "react-router-dom";

interface OrderToDispatch {
    entity_id: number;
    commande_fournisseur: {
        id: number;
        ref: string;
        fournisseur_nom?: string;
    };
    commande_client: {
        id: number;
        ref: string;
        date: string;
        jours?: number;
    };
    client: {
        nom: string;
        telephone: string;
    };
    produit: {
        id: number;
        ref: string;
        label: string;
    };
    ligne_commande: {
        id: number;
        description: string;
        subprice: number;
        total_ht: number;
        tva_tx: number;
    };
    quantites: {
        commandee: number;
        dispatch_fournisseur: number;
        expediee_client: number;
        restante: number;
    };
}

interface DispatchQuantityModalProps {
    isOpen: boolean;
    toggle: () => void;
    order: OrderToDispatch | null;
    onDispatch: (order: OrderToDispatch, quantity: number) => Promise<void>;
}

const DispatchQuantityModal: React.FC<DispatchQuantityModalProps> = ({
    isOpen,
    toggle,
    order,
    onDispatch
}) => {
    const [quantity, setQuantity] = useState<number>(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (order) {
            setQuantity(order.quantites.restante);
        }
    }, [order]);

    const handleSubmit = async () => {
        if (!order) return;
        setLoading(true);
        try {
            await onDispatch(order, quantity);
            toggle();
        } catch (error) {
            console.error('Error dispatching order:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setQuantity(0);
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose}>
            <ModalHeader toggle={handleClose}>
                <i className="ri-truck-line me-2"></i>
                Dispatch Order
            </ModalHeader>
            <ModalBody>
                {order && (
                    <div className="order-dispatch-info">
                        <div className="mb-3">
                            <h6>Product Details</h6>
                            <div className="small text-muted">
                                <strong>Reference:</strong> {order.produit.ref}
                            </div>
                            <div className="small text-muted">
                                <strong>Description:</strong> {order.produit.label}
                            </div>
                        </div>

                        <div className="mb-3">
                            <h6>Quantities</h6>
                            <div className="d-flex justify-content-between mb-2">
                                <div className="text-center">
                                    <div className="fw-bold">{order.quantites.commandee}</div>
                                    <small className="text-muted">Commandée</small>
                                </div>
                                <div className="text-center">
                                    <div className="fw-bold">{order.quantites.dispatch_fournisseur}</div>
                                    <small className="text-muted">Dispatch Fournisseur</small>
                                </div>
                                <div className="text-center">
                                    <div className="fw-bold">{order.quantites.expediee_client}</div>
                                    <small className="text-muted">Expédiée Client</small>
                                </div>
                                <div className="text-center">
                                    <div className="fw-bold text-primary">{order.quantites.restante}</div>
                                    <small className="text-muted">Restante</small>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <Label for="dispatchQuantity" className="form-label">
                                Quantité à Dispatcher
                            </Label>
                            <Input
                                type="number"
                                id="dispatchQuantity"
                                min="1"
                                max={order.quantites.restante}
                                value={quantity}
                                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                                disabled={loading}
                            />
                            <div className="form-text">
                                Maximum disponible: {order.quantites.restante}
                            </div>
                        </div>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={handleClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || !quantity || quantity > (order?.quantites.restante || 0)}
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Traitement...
                        </>
                    ) : (
                        <>
                            <i className="ri-check-line me-1"></i>
                            Dispatcher
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const OrdersToDispatchPage = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState<OrderToDispatch[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [countPerPage, setCountPerPage] = useState(0);
    
    // États de recherche
    const [searchRef, setSearchRef] = useState("");
    const [searchClient, setSearchClient] = useState("");
    const [searchSupplierOrderRef, setSearchSupplierOrderRef] = useState("");
    
    const [entityFilter, setEntityFilter] = useState<string | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<OrderToDispatch | null>(null);
    const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
    const [entities, setEntities] = useState<{ id: number; name: string }[]>([]);
    const navigate = useNavigate();

    // Fetch orders to dispatch
    const fetchOrdersToDispatch = async () => {
        setLoading(true);
        try {
            // Créer l'objet de recherche selon le format attendu par le backend
            const searchParams: any = {};
            
            if (searchRef) {
                searchParams.commande_ref = searchRef;
            }
            if (searchClient) {
                searchParams.client_name = searchClient;
            }
            if (searchSupplierOrderRef) {
                searchParams.commandefourn_ref = searchSupplierOrderRef;
            }

            // Construire le payload
            const payload: any = {
                action: 'orderstodispatch',
                entity: user.entity,
                page: page,
                limit: 100,
            };

            // Ajouter les paramètres de recherche seulement s'il y en a
            if (Object.keys(searchParams).length > 0) {
                payload.search = searchParams;
            }

            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            // LOGIQUE DE PAGINATION AMÉLIORÉE
            let orderLinesData: OrderToDispatch[] = [];
            let totalRecords = 0;
            let totalPagesValue = 1;
            let currentCount = 0;
            
            if (response.data && typeof response.data === 'object') {
                // Cas 1: response.data.data est un tableau (format avec métadonnées)
                if (response.data.data && Array.isArray(response.data.data)) {
                    orderLinesData = response.data.data as OrderToDispatch[];
                    totalRecords = response.data.nbtotalofrecords || response.data.total || 0;
                    totalPagesValue = response.data.totalpages || 1;
                    currentCount = response.data.count || response.data.data.length;
                } 
                // Cas 2: response.data est directement un tableau
                else if (Array.isArray(response.data)) {
                    orderLinesData = response.data as OrderToDispatch[];
                    currentCount = response.data.length;
                    totalRecords = (response as any).nbtotalofrecords || response.data.length;
                    totalPagesValue = (response as any).totalpages || 1;
                }
            }

            // Mettre à jour les états
            setOrders(orderLinesData || []);
            setTotalCount(totalRecords);
            setTotalPages(totalPagesValue);
            setCountPerPage(currentCount);

        } catch (error) {
            console.error('Error fetching orders to dispatch:', error);
            setOrders([]);
            setTotalCount(0);
            setTotalPages(1);
            setCountPerPage(0);
        } finally {
            setLoading(false);
        }
    };

    // Naviguer vers la livraison
    const handleLivrerOrder = (order: OrderToDispatch) => {
        navigate('/CommandesEnAttentes', {
            replace: true,
            state: {
                autoSelectOrderId: order.commande_client.id,
                autoSelectOrderRef: order.commande_client.ref,
                fromLivrer: true
            }
        });
    };

    // Handle dispatch action
    const handleDispatchOrder = async (order: OrderToDispatch, quantity: number) => {
        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'dispatchorder',
                    entity: user.entity,
                    order_id: order.commande_fournisseur.id,
                    client_order_id: order.commande_client.id,
                    product_id: order.produit.id,
                    line_id: order.ligne_commande.id,
                    quantity: quantity
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            // LOGIQUE DE RÉPONSE SIMILAIRE
            if (response.data && typeof response.data === 'object') {
                if (response.data.success !== false) {
                    // Navigate to WaitingOrders page with the order information
                    navigate('/waiting-orders', {
                        state: {
                            orderId: order.commande_client.id,
                            orderRef: order.commande_client.ref,
                            autoSelect: true
                        }
                    });
                    return;
                }
                throw new Error(response.data.message || 'Failed to dispatch order');
            }
            throw new Error('Invalid response format');
        } catch (error) {
            console.error('Error dispatching order:', error);
            throw error;
        }
    };

    // Open dispatch modal
    const openDispatchModal = (order: OrderToDispatch) => {
        setSelectedOrder(order);
        setDispatchModalOpen(true);
    };

    // Handle search
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchOrdersToDispatch();
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Clear filters
    const clearFilters = () => {
        setSearchRef("");
        setSearchClient("");
        setSearchSupplierOrderRef("");
        setEntityFilter(null);
        setPage(1);
        fetchOrdersToDispatch();
    };

    // Calculate unique entities from orders
    useEffect(() => {
        const uniqueEntities = Array.from(
            new Set(orders.map(order => order.entity_id))
        ).map(id => ({
            id,
            name: `Entité ${id}`
        }));
        setEntities(uniqueEntities);
    }, [orders]);

    // Fetch data when dependencies change
    useEffect(() => {
        fetchOrdersToDispatch();
    }, [page, entityFilter]);

    // SmartPagination component
    const SmartPagination: React.FC = () => {
        const maxVisiblePages = 5;
        const halfVisible = Math.floor(maxVisiblePages / 2);
        const [pageInput, setPageInput] = useState(page.toString());

        useEffect(() => {
            setPageInput(page.toString());
        }, [page]);

        let startPage = Math.max(1, page - halfVisible);
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        const handlePageJump = () => {
            const newPage = parseInt(pageInput);
            if (!isNaN(newPage)) {
                const clampedPage = Math.max(1, Math.min(totalPages, newPage));
                setPage(clampedPage);
                fetchOrdersToDispatch();
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                handlePageJump();
            }
        };

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Affichage <strong>{countPerPage}</strong> ligne(s)
                </div>

                <Pagination className="mb-0">
                    <PaginationItem disabled={page === 1 || loading}>
                        <PaginationLink first onClick={() => handlePageChange(1)} />
                    </PaginationItem>
                    <PaginationItem disabled={page === 1 || loading}>
                        <PaginationLink previous onClick={() => handlePageChange(page - 1)} />
                    </PaginationItem>

                    {startPage > 1 && (
                        <>
                            <PaginationItem disabled={loading}>
                                <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                            </PaginationItem>
                            {startPage > 2 && (
                                <PaginationItem disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            )}
                        </>
                    )}

                    {pages.map(pageNum => (
                        <PaginationItem active={pageNum === page} key={pageNum} disabled={loading}>
                            <PaginationLink onClick={() => handlePageChange(pageNum)}>
                                {pageNum}
                            </PaginationLink>
                        </PaginationItem>
                    ))}

                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <PaginationItem disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            )}
                            <PaginationItem disabled={loading}>
                                <PaginationLink onClick={() => handlePageChange(totalPages)}>
                                    {totalPages}
                                </PaginationLink>
                            </PaginationItem>
                        </>
                    )}

                    <PaginationItem disabled={page === totalPages || loading}>
                        <PaginationLink next onClick={() => handlePageChange(page + 1)} />
                    </PaginationItem>
                    <PaginationItem disabled={page === totalPages || loading}>
                        <PaginationLink last onClick={() => handlePageChange(totalPages)} />
                    </PaginationItem>
                </Pagination>

                <div className="d-flex align-items-center gap-1">
                    <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ width: '60px' }}
                        className="text-center"
                        bsSize="sm"
                        disabled={loading}
                    />
                    <Button
                        color="primary"
                        size="sm"
                        onClick={handlePageJump}
                        disabled={loading}
                    >
                        {loading ? <Spinner size="sm" /> : 'Aller'}
                    </Button>
                </div>
            </div>
        );
    };

    // Table columns
    const columns = [
        {
            header: "Commande Client",
            accessorKey: "commande_client",
            cell: (cell: any) => {
                const commande = cell.getValue();
                return (
                    <div>
                        <div className="fw-semibold">{commande.ref}</div>
                        <small className={commande.jours > 3 ? "text-danger" : "text-muted"}>
                            {new Date(commande.date).toLocaleDateString()}
                            {commande.jours > 3 && ` (${commande.jours} jours)`}
                        </small>
                    </div>
                );
            }
        },
        {
            header: "Commande Fournisseur",
            accessorKey: "commande_fournisseur",
            cell: (cell: any) => {
                const commande = cell.getValue();
                return (
                    <div>
                        <div className="fw-semibold">{commande.ref}</div>
                        {commande.fournisseur_nom && (
                            <small className="text-muted">{commande.fournisseur_nom}</small>
                        )}
                    </div>
                );
            }
        },
        {
            header: "Client",
            accessorKey: "client",
            cell: (cell: any) => {
                const client = cell.getValue();
                return (
                    <div>
                        <div className="fw-semibold">{client.nom}</div>
                        {client.telephone && (
                            <small className="text-muted">{client.telephone}</small>
                        )}
                    </div>
                );
            }
        },
        {
            header: "Produit",
            accessorKey: "produit",
            cell: (cell: any) => {
                const produit = cell.getValue();
                return (
                    <div>
                        <div className="fw-semibold">{produit.ref}</div>
                        <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }}>
                            {produit.label}
                        </small>
                    </div>
                );
            }
        },
        {
            header: "Quantités",
            accessorKey: "quantites",
            cell: (cell: any) => {
                const quantites = cell.getValue();
                return (
                    <div className="d-flex flex-column">
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Commandée:</span>
                            <span className="fw-semibold">{quantites.commandee}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Restante:</span>
                            <span className="fw-semibold text-primary">{quantites.restante}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Reçu:</span>
                            <span className="fw-semibold">
                                {quantites.dispatch_fournisseur > quantites.restante ? 
                                 quantites.restante : quantites.dispatch_fournisseur}
                            </span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Expédiée:</span>
                            <span className="fw-semibold">{quantites.expediee_client}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            header: "Prix",
            accessorKey: "ligne_commande",
            cell: (cell: any) => {
                const ligne = cell.getValue();
                return (
                    <div>
                        <div>Unité: {ligne.subprice.toFixed(2)} €</div>
                        <div>Total HT: {ligne.total_ht.toFixed(2)} €</div>
                        
                    </div>
                );
            }
        },
    ];

    // Ajouter la colonne Action conditionnellement
    if (user?.perms?.stock?.mouvement?.creer != null && user?.perms?.commande?.creer == null) {
        columns.push({
            header: "Action",
            accessorKey: "quantites.restante",
            cell: (cell: any) => {
                const remaining = cell.getValue();
                const order = cell.row.original;

                const canDispatch = order.quantites.dispatch_fournisseur >= order.quantites.restante;
                const isDisabled = remaining === 0 || loading || !canDispatch;
                return (
                    <div className="d-flex justify-content-center">
                        <Button
                            color={!isDisabled ? "success" : "secondary"}
                            size="sm"
                            onClick={() => !isDisabled && handleLivrerOrder(order)}
                            disabled={isDisabled}
                            className="d-flex align-items-center"
                        >
                            <i className="ri-truck-line me-1"></i>
                            Livrer
                        </Button>
                    </div>
                );
            }
        });
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between mb-3">
                                    <h4 className="card-title mb-0">
                                        <i className="ri-truck-line align-bottom me-2"></i>
                                        Pièces en attentes
                                    </h4>
                                    <div className="text-muted">
                                        Total: <strong>{totalCount}</strong> ligne(s) trouvée(s)
                                    </div>
                                </div>

                                {/* Search and Filters */}
                                <form onSubmit={handleSearch} className="mb-4">
                                    <Row className="g-3 align-items-end">
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label for="searchRef" className="form-label small text-muted mb-1">
                                                    RÉFÉRENCE COMMANDE CLIENT
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-barcode-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        id="searchRef"
                                                        placeholder="Référence commande client..."
                                                        value={searchRef}
                                                        onChange={(e) => setSearchRef(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label for="searchSupplierOrderRef" className="form-label small text-muted mb-1">
                                                    RÉFÉRENCE COMMANDE FOURNISSEUR
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-shopping-bag-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        id="searchSupplierOrderRef"
                                                        placeholder="Référence commande fournisseur..."
                                                        value={searchSupplierOrderRef}
                                                        onChange={(e) => setSearchSupplierOrderRef(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label for="searchClient" className="form-label small text-muted mb-1">
                                                    CLIENT
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-user-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        id="searchClient"
                                                        placeholder="Nom client..."
                                                        value={searchClient}
                                                        onChange={(e) => setSearchClient(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2} className="ms-auto">
                                            <FormGroup className="mb-0">
                                                <Label className="form-label small text-muted mb-1 d-block">
                                                    &nbsp;
                                                </Label>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        type="submit"
                                                        color="primary"
                                                        className="flex-grow-1"
                                                        style={{ height: "42px" }}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <Spinner size="sm" />
                                                        ) : (
                                                            <>
                                                                <i className="ri-search-line align-bottom me-1"></i>
                                                                Rechercher
                                                            </>
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        color="light"
                                                        style={{ height: "42px" }}
                                                        onClick={clearFilters}
                                                        disabled={loading}
                                                    >
                                                        <i className="ri-eraser-line"></i>
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </form>

                                {/* Table */}
                                {loading && orders.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" />
                                        <div className="mt-2">Chargement des commandes...</div>
                                    </div>
                                ) : (
                                    <>
                                        {orders.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ri-inbox-line display-4 text-muted mb-3"></i>
                                                <h5 className="mb-2">Aucune commande à dispatcher</h5>
                                                <p className="text-muted mb-4">
                                                    Il n'y a actuellement aucune commande en attente de dispatch.
                                                </p>
                                                <div className="d-flex justify-content-center gap-3">
                                                    <Button 
                                                        color="primary" 
                                                        onClick={clearFilters}
                                                        className="d-flex align-items-center"
                                                    >
                                                        <i className="ri-refresh-line me-2"></i>
                                                        Actualiser la liste
                                                    </Button>
                                                    <Button 
                                                        color="light" 
                                                        onClick={() => navigate('/')}
                                                        className="d-flex align-items-center"
                                                    >
                                                        <i className="ri-home-line me-2"></i>
                                                        Retour à l'accueil
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="position-relative">
                                                    {loading && (
                                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                                                            <div className="text-center">
                                                                <Spinner color="primary" />
                                                                <div className="mt-2">Chargement...</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <TableContainer
                                                        columns={columns}
                                                        data={orders}
                                                        isGlobalFilter={false}
                                                        customPageSize={countPerPage}
                                                        divClass="table-responsive"
                                                        tableClass="align-middle table-nowrap"
                                                        theadClass="table-light text-muted"
                                                    />
                                                </div>

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <SmartPagination />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Dispatch Modal */}
            <DispatchQuantityModal
                isOpen={dispatchModalOpen}
                toggle={() => setDispatchModalOpen(false)}
                order={selectedOrder}
                onDispatch={handleDispatchOrder}
            />
        </div>
    );
};

export default OrdersToDispatchPage;